import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  PictureAsPdf as PictureAsPdfIcon,
  Search as SearchIcon,
  TableChart as TableChartIcon,
  VisibilityOutlined as VisibilityOutlinedIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  format,
  parseISO,
  isValid,
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  subMonths
} from 'date-fns';
import * as XLSX from 'xlsx';
import PDFViewer from '../../components/shared/PDFViewer';
import libroMayorService, { LibroMayorResult } from '../../services/libroMayorService';
import imputableService from '../../services/imputableService';
import type { Imputable } from '../../services/imputableService';

/** Chips de período rápido; la API interpreta fecha hasta como día inclusivo (límite exclusivo interno). */
type QuickChip = 'hoy' | 'semana' | 'mes' | 'mesPasado' | '3meses' | '6meses';

const QUICK_CHIP_KEYS: QuickChip[] = ['hoy', 'semana', 'mes', 'mesPasado', '3meses', '6meses'];

function computeQuickRange(which: QuickChip): { desde: string; hasta: string } {
  const now = new Date();
  switch (which) {
    case 'hoy': {
      const d = format(now, 'yyyy-MM-dd');
      return { desde: d, hasta: d };
    }
    case 'semana':
      return {
        desde: format(startOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        hasta: format(endOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd')
      };
    case 'mes':
      return {
        desde: format(startOfMonth(now), 'yyyy-MM-dd'),
        hasta: format(endOfMonth(now), 'yyyy-MM-dd')
      };
    case 'mesPasado': {
      const ref = subMonths(now, 1);
      return {
        desde: format(startOfMonth(ref), 'yyyy-MM-dd'),
        hasta: format(endOfMonth(ref), 'yyyy-MM-dd')
      };
    }
    case '3meses':
      return {
        desde: format(startOfDay(subMonths(now, 3)), 'yyyy-MM-dd'),
        hasta: format(endOfDay(now), 'yyyy-MM-dd')
      };
    case '6meses':
      return {
        desde: format(startOfDay(subMonths(now, 6)), 'yyyy-MM-dd'),
        hasta: format(endOfDay(now), 'yyyy-MM-dd')
      };
    default: {
      const _exhaustive: never = which;
      return _exhaustive;
    }
  }
}

function findMatchingQuickChip(desde: string, hasta: string): QuickChip | null {
  for (const key of QUICK_CHIP_KEYS) {
    const r = computeQuickRange(key);
    if (desde === r.desde && hasta === r.hasta) return key;
  }
  return null;
}

function formatFecha(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = parseISO(iso);
    return isValid(d) ? format(d, 'dd/MM/yyyy') : iso;
  } catch {
    return iso;
  }
}

function fmtImporte(n: number): string {
  return n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const CuentaAutocomplete: React.FC<{
  nroCuenta: number | '';
  nomDisplay: string;
  onPick: (nro: number, nom: string) => void;
}> = ({ nroCuenta, nomDisplay, onPick }) => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<Imputable[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        setLoading(true);
        const q = input.trim() || (nroCuenta !== '' ? String(nroCuenta) : '');
        const res = await imputableService.getImputablesSearchPaged({
          pageNumber: 1,
          pageSize: 20,
          searchTerm: q
        });
        const raw = res.items as unknown as Record<string, unknown>[];
        setOptions(
          raw.map((item) => ({
            nroCuenta: Number(item.nroCuenta ?? item.NroCuenta ?? 0),
            nomCuenta: String(item.nomCuenta ?? item.NomCuenta ?? '')
          }))
        );
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [input, nroCuenta]);

  const value: Imputable | null = useMemo(() => {
    if (nroCuenta === '' || nroCuenta == null) return null;
    return {
      nroCuenta: Number(nroCuenta),
      nomCuenta: nomDisplay || String(nroCuenta)
    };
  }, [nroCuenta, nomDisplay]);

  return (
    <Autocomplete
      loading={loading}
      options={options}
      filterOptions={(x) => x}
      getOptionLabel={(o) => (o?.nroCuenta != null ? `${o.nroCuenta} — ${o.nomCuenta ?? ''}` : '')}
      isOptionEqualToValue={(a, b) => a?.nroCuenta === b?.nroCuenta}
      value={value}
      onChange={(_, v) => {
        if (v?.nroCuenta != null) onPick(v.nroCuenta, v.nomCuenta ?? '');
      }}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      renderInput={(params) => <TextField {...params} size="small" label="Cuenta imputable" placeholder="Buscar…" />}
      sx={{ minWidth: 280, flex: 1, maxWidth: 420 }}
    />
  );
};

const LibroMayor: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();

  const [fechaDesde, setFechaDesde] = useState(() => computeQuickRange('mes').desde);
  const [fechaHasta, setFechaHasta] = useState(() => computeQuickRange('mes').hasta);

  const presetActivo = useMemo(() => findMatchingQuickChip(fechaDesde, fechaHasta), [fechaDesde, fechaHasta]);
  const periodoPersonalizado = Boolean(fechaDesde && fechaHasta && presetActivo === null);

  const [nroCuenta, setNroCuenta] = useState<number | ''>('');
  const [nomCuentaSel, setNomCuentaSel] = useState('');

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<LibroMayorResult | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const previewPdfUrlRef = useRef<string | null>(null);
  previewPdfUrlRef.current = previewPdfUrl;

  const applyQuickChip = useCallback((which: QuickChip) => {
    const r = computeQuickRange(which);
    setFechaDesde(r.desde);
    setFechaHasta(r.hasta);
  }, []);

  /** Desde libro diario u otro enlace: ?cuenta=&fechaDesde=&fechaHasta=&nomCuenta= */
  useEffect(() => {
    const cuenta = searchParams.get('cuenta');
    const fd = searchParams.get('fechaDesde');
    const fh = searchParams.get('fechaHasta');
    const nom = searchParams.get('nomCuenta');
    if (cuenta != null && cuenta !== '') {
      const n = parseInt(cuenta, 10);
      if (Number.isFinite(n) && n > 0) {
        setNroCuenta(n);
        if (nom != null && nom.trim() !== '') setNomCuentaSel(nom.trim());
      }
    }
    if (fd != null && /^\d{4}-\d{2}-\d{2}$/.test(fd)) setFechaDesde(fd);
    if (fh != null && /^\d{4}-\d{2}-\d{2}$/.test(fh)) setFechaHasta(fh);
  }, [searchParams]);

  const cargarPagina = useCallback(
    async (pageIndex: number, size: number) => {
      if (nroCuenta === '' || !Number.isFinite(Number(nroCuenta))) {
        enqueueSnackbar('Seleccioná una cuenta imputable.', { variant: 'warning' });
        return;
      }
      if (!fechaDesde || !fechaHasta) {
        enqueueSnackbar('Indicá fecha desde y fecha hasta.', { variant: 'warning' });
        return;
      }
      try {
        setLoading(true);
        const data = await libroMayorService.getLibroMayor({
          fechaDesde,
          fechaHasta,
          nroCuenta: Number(nroCuenta),
          pageNumber: pageIndex + 1,
          pageSize: size
        });
        setResult(data);
        setPage(Math.max(0, (data.pageNumber ?? 1) - 1));
      } catch (e) {
        console.error(e);
        enqueueSnackbar(libroMayorService.parseError(e), { variant: 'error' });
        setResult(null);
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar, fechaDesde, fechaHasta, nroCuenta]
  );

  const ejecutarConsulta = useCallback(() => {
    setPage(0);
    void cargarPagina(0, rowsPerPage);
  }, [cargarPagina, rowsPerPage]);

  const handleChangePage = useCallback(
    (_: unknown, newPage: number) => {
      void cargarPagina(newPage, rowsPerPage);
    },
    [cargarPagina, rowsPerPage]
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = parseInt(event.target.value, 10);
      setRowsPerPage(next);
      setPage(0);
      void cargarPagina(0, next);
    },
    [cargarPagina]
  );

  useEffect(() => {
    return () => {
      if (previewPdfUrlRef.current) URL.revokeObjectURL(previewPdfUrlRef.current);
    };
  }, []);

  const filtrosExportOk = useMemo(
    () =>
      nroCuenta !== '' &&
      Number.isFinite(Number(nroCuenta)) &&
      Boolean(fechaDesde?.trim()) &&
      Boolean(fechaHasta?.trim()),
    [nroCuenta, fechaDesde, fechaHasta]
  );

  const buildExportParams = useCallback((): {
    fechaDesde: string;
    fechaHasta: string;
    nroCuenta: number;
  } | null => {
    if (!filtrosExportOk) return null;
    return {
      fechaDesde,
      fechaHasta,
      nroCuenta: Number(nroCuenta)
    };
  }, [fechaDesde, fechaHasta, filtrosExportOk, nroCuenta]);

  const handleClosePdfPreview = useCallback(() => {
    setPreviewPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const handlePreviewPdfLibroMayor = useCallback(async () => {
    const p = buildExportParams();
    if (!p) {
      enqueueSnackbar('Seleccioná cuenta y fechas.', { variant: 'warning' });
      return;
    }
    try {
      setPdfLoading(true);
      const blob = await libroMayorService.getPreviewPdfBlob(p);
      const safeBlob =
        blob instanceof Blob && blob.type === 'application/pdf'
          ? blob
          : new Blob([blob], { type: 'application/pdf' });
      const url = URL.createObjectURL(safeBlob);
      setPreviewPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      enqueueSnackbar('Vista previa del PDF lista.', { variant: 'success' });
    } catch (e) {
      console.error(e);
      enqueueSnackbar(e instanceof Error ? e.message : libroMayorService.parseError(e), { variant: 'error' });
    } finally {
      setPdfLoading(false);
    }
  }, [buildExportParams, enqueueSnackbar]);

  const handleExportExcel = useCallback(async () => {
    const p = buildExportParams();
    if (!p) {
      enqueueSnackbar('Seleccioná cuenta y fechas.', { variant: 'warning' });
      return;
    }
    try {
      setExcelLoading(true);
      const data = await libroMayorService.getExportData(p);
      const aoa: (string | number)[][] = [
        ['Libro mayor — resumen'],
        ['Cuenta', data.nroCuenta, data.nomCuenta ?? ''],
        ['Período desde', formatFecha(data.fechaDesde), ''],
        ['Período hasta', formatFecha(data.fechaHasta), ''],
        ['Movimientos en el período', data.totalMovimientos, ''],
        [],
        ['Fecha', 'Nº asiento', 'Comprobante', 'Detalle', 'Debe', 'Haber', 'Saldo']
      ];
      aoa.push([
        formatFecha(data.fechaDesde),
        '',
        '',
        'Saldo inicial',
        '',
        '',
        data.saldoInicial
      ]);
      for (const ln of data.lineas) {
        aoa.push([
          ln.fechaAsiento ? formatFecha(ln.fechaAsiento) : '',
          ln.nroAsiento,
          ln.comprobante,
          ln.detalle ?? '',
          ln.debe,
          ln.haber,
          ln.saldo
        ]);
      }
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Libro mayor');
      const slug = `${data.nroCuenta}_${p.fechaDesde}_${p.fechaHasta}`;
      XLSX.writeFile(wb, `libro_mayor_${slug}.xlsx`);
      enqueueSnackbar('Excel generado.', { variant: 'success' });
    } catch (e) {
      console.error(e);
      enqueueSnackbar(libroMayorService.parseError(e), { variant: 'error' });
    } finally {
      setExcelLoading(false);
    }
  }, [buildExportParams, enqueueSnackbar]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Libro mayor
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => void handlePreviewPdfLibroMayor()}
                disabled={!filtrosExportOk || pdfLoading}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
              >
                {pdfLoading ? 'Generando PDF…' : 'Libro mayor'}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<TableChartIcon />}
                onClick={() => void handleExportExcel()}
                disabled={!filtrosExportOk || excelLoading}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
              >
                {excelLoading ? 'Exportando…' : 'Exportar Excel'}
              </Button>
            </Stack>
          </Stack>

          <Card sx={{ mb: 3, bgcolor: '#fafafa', borderRadius: 2, boxShadow: 'none', border: '1px solid rgba(0,0,0,0.06)' }}>
            <CardContent sx={{ py: 2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap" useFlexGap alignItems="flex-end">
                <CuentaAutocomplete
                  nroCuenta={nroCuenta}
                  nomDisplay={nomCuentaSel}
                  onPick={(nro, nom) => {
                    setNroCuenta(nro);
                    setNomCuentaSel(nom);
                  }}
                />
                <TextField
                  size="small"
                  label="Fecha desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 170, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                />
                <TextField
                  size="small"
                  label="Fecha hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 170, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SearchIcon />}
                  onClick={() => ejecutarConsulta()}
                  disabled={loading}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                    boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)'
                  }}
                >
                  {loading ? 'Consultando…' : 'Consultar'}
                </Button>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }} alignItems="center">
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                  Período rápido
                </Typography>
                {(
                  [
                    { key: 'hoy' as const, label: 'Hoy' },
                    { key: 'semana' as const, label: 'Semana actual' },
                    { key: 'mes' as const, label: 'Mes actual' },
                    { key: 'mesPasado' as const, label: 'Mes pasado' },
                    { key: '3meses' as const, label: 'Últ. 3 meses' },
                    { key: '6meses' as const, label: 'Últ. 6 meses' }
                  ] as const
                ).map(({ key, label }) => (
                  <Chip
                    key={key}
                    label={label}
                    size="small"
                    onClick={() => applyQuickChip(key)}
                    color={presetActivo === key ? 'primary' : 'default'}
                    variant={presetActivo === key ? 'filled' : 'outlined'}
                  />
                ))}
                <Chip
                  label="Personalizado"
                  size="small"
                  color={periodoPersonalizado ? 'primary' : 'default'}
                  variant={periodoPersonalizado ? 'filled' : 'outlined'}
                  title="Las fechas no coinciden con un período rápido; podés editarlas arriba."
                  sx={{ cursor: 'default' }}
                />
              </Stack>
            </CardContent>
          </Card>

          {result && (
            <Card sx={{ mb: 2, bgcolor: '#e3f2fd', borderRadius: 2, boxShadow: 'none', border: '1px solid rgba(25,118,210,0.2)' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {result.nroCuenta} — {result.nomCuenta ?? '—'}
                </Typography>
              </CardContent>
            </Card>
          )}

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Nº asiento</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Comprobante</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Detalle</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Debe
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Haber
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Saldo
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    Ver
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={8}>Cargando…</TableCell>
                  </TableRow>
                )}
                {!loading && result && page === 0 && (
                  <TableRow
                    key="saldo-inicial"
                    sx={{ backgroundColor: 'rgba(25, 118, 210, 0.06)', '& td': { fontWeight: 500 } }}
                  >
                    <TableCell>{formatFecha(fechaDesde)}</TableCell>
                    <TableCell>—</TableCell>
                    <TableCell>—</TableCell>
                    <TableCell>Saldo inicial</TableCell>
                    <TableCell align="right">—</TableCell>
                    <TableCell align="right">—</TableCell>
                    <TableCell align="right">{fmtImporte(result.saldoInicial)}</TableCell>
                    <TableCell align="center">—</TableCell>
                  </TableRow>
                )}
                {!loading &&
                  result?.lineas.map((row) => (
                    <TableRow key={`${row.nroAsiento}-${row.nroId}`} hover>
                      <TableCell>{formatFecha(row.fechaAsiento)}</TableCell>
                      <TableCell>{row.nroAsiento}</TableCell>
                      <TableCell>{row.comprobante}</TableCell>
                      <TableCell>{row.detalle ?? '—'}</TableCell>
                      <TableCell align="right">{row.debe !== 0 ? fmtImporte(row.debe) : '—'}</TableCell>
                      <TableCell align="right">{row.haber !== 0 ? fmtImporte(row.haber) : '—'}</TableCell>
                      <TableCell align="right">{fmtImporte(row.saldo)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver asiento (nueva pestaña)">
                          <IconButton
                            size="small"
                            color="primary"
                            component={Link}
                            to={`/contabilidad/asientos-diario/${row.nroAsiento}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                {!loading && !result && (
                  <TableRow>
                    <TableCell colSpan={8} color="text.secondary">
                      Elegí cuenta y fechas y pulsá Consultar.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {result !== null && (
            <TablePagination
              component="div"
              count={result.totalMovimientos}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Movimientos por página"
              labelDisplayedRows={({ from, to, count }) =>
                count === 0 ? '0 movimientos' : `${from}-${to} de ${count} movimientos`
              }
            />
          )}
        </CardContent>
      </Card>

      {previewPdfUrl ? (
        <PDFViewer
          pdfUrl={previewPdfUrl}
          onClose={handleClosePdfPreview}
          title="Libro mayor"
          documentId="libro-mayor-preview"
          documentType="informeCliente"
          downloadFileName="libro-mayor.pdf"
        />
      ) : null}
    </Box>
  );
};

export default LibroMayor;
